<template>
	<div>
		<app-app-header v-model="indexStore.isHeaderVisible"
						:title="headerStyle.title"
						:breadcrumbs="headerStyle.breadcrumbs"
						:color="headerStyle.color"
						:image="headerStyle.avatar"
						:item-link="headerStyle.itemLink" hide-on-scroll/>

		<app-content>
			<app-header-toolbar :color="headerStyle.color" :on-top="!indexStore.isHeaderVisible" :item-link="headerStyle.itemLink">
				<div class="w-full h-full flex items-center">
					<form-input name="search"
								v-model="filterData.search"
								:icon="icons.search"
								type="search"
								enterkeyhint="search"
								hide-details
								inverted
								clearable
								class="flex-grow-x-1"
								@focus="handleSearchInputFocus"
								@blur="handleSearchInputBlur"
								:placeholder="$t('placeholder.search_job_postings')"/>

					<btn class="ml-8 sm:min-w-320" @click="openFilterModal" :color="inputFilterExists ? (headerStyle?.color ?? 'primary') : null">
						<icon :icon="icons.filter"/>
						<span class="none sm:block">{{ $t('action.filter') }}</span>
					</btn>
					<btn v-if="inputFilterExists" class="ml-8 none sm:flex" @click="resetFilterModal">
						<icon :icon="icons.close"/>
						<span>{{ $t('action.reset') }}</span>
					</btn>
				</div>

				<template #tabs>
					<tabs grow>
						<tab v-for="(tab, tabIndex) in typePageConfigs" :key="tabIndex" :to="{path: tab.to, query: $route.query}" replace
							 :color="headerStyle?.color ?? 'primary'" :text=" tab.text"/>
					</tabs>
				</template>

			</app-header-toolbar>

			<page-content background xs-full sm-full>
				<container xs-full sm-full class="stretch-layout mt-8 md:mt-0'">
					<card v-if="activeTypePage.key === 'all'" xs-tile sm-tile>
						<data-loader :url="activeTypePage.apiUrl"
									 on-scroll
									 :persist-filter-keys="filterProps"
									 :filter="filterData"
									 :params="activeTypePage.params" @filtering="handleApplyingFilter">

							<template #loading>
								<job-list-item-skeleton v-for="i in 10" :key="i"/>
							</template>

							<template v-slot="{data}">
								<job-list-item v-for="jobPosting in data" :key="`job-${jobPosting.id}`" :job="jobPosting"/>
							</template>

							<template #missing>
								<missing-info :text="$t('missing.job.index')" class="min-h-200" no-spacing tile/>
							</template>
							<template #missingsearch>
								<missing-info :text="$t('missing.search')" class="min-h-200" no-spacing tile/>
							</template>

						</data-loader>
					</card>

					<data-loader v-else :url="activeTypePage.apiUrl"
								 on-scroll
								 :persist-filter-keys="filterProps"
								 :params="activeTypePage.params"
								 :filter="filterData" @filtering="handleApplyingFilter">

						<template #loading>
							<card v-for="i in activeTypePage.params.limit" :key="i" xs-tile sm-tile class="border-t-2 border-gray-300">
								<profile-preview-skeleton flat :event="$route.params.type === 'events'" class="border-b px-16 sm:px-24"/>
								<job-list-item-skeleton v-for="j in jobLimitPerOwner" :key="`job-skeleton-${i}-${j}`"/>
							</card>
						</template>

						<template v-slot="{data}">
							<card v-for="(listItem, listItemIndex) in data" :key="`job-set-${listItemIndex}`" xs-tile sm-tile
								  :style="{borderTop: `2px solid ${activeTypePage.jobOwnerResolver(listItem).color ?? '#008200'}`}">
								<profile-preview :model="activeTypePage.jobOwnerResolver(listItem)" flat class="px-16 sm:px-24" :class="{'border-b': listItem.jobPostings.length}"
												 :to="{path: activeTypePage.jobOwnerPathResolver(listItem), query: $route.query}"/>

								<template v-if="listItem.jobPostings.length">
									<job-list-item v-for="jobPosting in listItem.jobPostings" :key="`job-${jobPosting.id}`" :job="jobPosting"
												   :to="activeTypePage.jobPathResolver(listItem, jobPosting)"/>

									<btn v-if="listItem.jobPostings.length >= jobLimitPerOwner" text large class="rounded-t-none border-t"
										 :to="{path: activeTypePage.jobOwnerPathResolver(listItem), query: $route.query}"
										 :color="activeTypePage.jobOwnerResolver(listItem).color ?? 'primary'">
										<span>{{ $t('action.show_all') }}</span>
										<icon :icon="icons.next"/>
									</btn>
								</template>
							</card>
						</template>

						<template #missing>
							<missing-info :text="$t('missing.job.index')" class="min-h-200" xs-tile sm-tile card/>
						</template>
						<template #missingsearch>
							<missing-info :text="$t('missing.search')" class="min-h-200" xs-tile sm-tile card/>
						</template>

					</data-loader>
				</container>
			</page-content>

			<modal v-model="showFilterModal" :title="$t('action.filter')" small :show-close-button="false">
				<form-input name="search"
							v-model="inputFilterData.search"
							type="search"
							inverted
							clearable
							:placeholder="$t('placeholder.search')"
							:icon="icons.search"/>

				<p class="subheading mb-16">{{ $t('label.operation_time') }}</p>

				<lazy-form-date-input v-model="inputFilterData.min_date" name="min_date" inverted
									  clearable
									  :rules="{max: inputFilterData.max_date}"
									  :label="$t('input.event.min_date')"/>
				<lazy-form-date-input v-model="inputFilterData.max_date" name="max_date" inverted
									  clearable
									  :rules="{min: inputFilterData.min_date}"
									  :label="$t('input.event.max_date')"/>

				<p class="subheading mb-16">{{ $t('label.operation_location') }}</p>

				<lazy-form-switch v-model="inputFilterData.only_remote" @update:modelValue="value => hideAddressFilter = value" name="only_remote"
								  :label="$t('question.show_only_remote_jobs')"/>

				<template v-if="!hideAddressFilter">
					<lazy-form-country-select v-model="inputFilterData.country" name="country" :label="$t('label.country')" filterable inverted clearable/>
					<lazy-form-state-select v-model="inputFilterData.state" :country="inputFilterData.country" name="state"
											:label="$t('label.country_state')" filterable inverted clearable/>
					<lazy-form-input v-model="inputFilterData.city" name="city" clearable inverted :label="$t('label.city')"/>
				</template>

				<template #footer>
					<wrapper class="p-16 border-box border-t" stretch>
						<btn v-if="inputFilterExists" @click="resetFilterModal" large>
							<icon :icon="icons.close"/>
							<span>{{ $t('action.reset') }}</span>
						</btn>
						<btn @click="submitFilterModal" large color="success">
							<icon :icon="icons.submit"/>
							<span>{{ $t('action.filter') }}</span>
						</btn>
					</wrapper>
				</template>
			</modal>
		</app-content>
	</div>
</template>

<script setup lang="ts">
import {useIndexStore} from "../../../store";
import {mdiFilterVariant} from "@mdi/js";
import {closeIcon, nextIcon, searchIcon, submitIcon} from "@spoferan/nuxt-spoferan/icons";
import {GeolocationService} from "@spoferan/nuxt-spoferan/services";
import {useFilterableOnJobs} from "../../../composables/useFilterableOnJobs";

const indexStore = useIndexStore();
const {t} = useI18n();
const config = useRuntimeConfig();
const route = useRoute();
const {$web, $auth, $api} = useNuxtApp();
const {
	handleApplyingFilter,
	handleSearchInputFocus,
	handleSearchInputBlur
} = useSearchPage();

const {
	filterProps,
	filterData,
	showFilterModal,
	inputFilterData,
	hideAddressFilter,
	inputFilterExists,
	openFilterModal,
	submitFilterModal,
	resetFilterModal
} = useFilterableOnJobs();

const jobLimitPerOwner = 3;

const typePageConfigs = {
	all: {
		key: 'all',
		text: t('label.all'),
		to: $web('jobs.index'),
		apiUrl: $api('jobs.index'),
		params: {
			relations: ['event', 'club']
		}
	},
	events: {
		key: 'events',
		text: t('label.events'),
		to: $web('jobs.events.index'),
		apiUrl: $api('jobs.events.index'),
		params: {
			limit: 4,
			job_limit: jobLimitPerOwner
		},
		jobOwnerResolver: (listItem) => listItem.event,
		jobOwnerPathResolver: (listItem) => $web('jobs.events.show', [listItem.event._key]),
		jobPathResolver: (listItem, jobPosting) => $web('jobs.events.jobs.show', [listItem.event._key, jobPosting._key])
	},
	clubs: {
		key: 'clubs',
		text: t('label.clubs'),
		to: $web('jobs.clubs.index'),
		apiUrl: $api('jobs.clubs.index'),
		params: {
			limit: 10,
			type: 'club',
			job_limit: jobLimitPerOwner
		},
		jobOwnerResolver: (listItem) => listItem.club,
		jobOwnerPathResolver: (listItem) => $web('jobs.clubs.show', [listItem.club._key]),
		jobPathResolver: (listItem, jobPosting) => jobPosting.event ? $web('jobs.events.jobs.show', [jobPosting.event._key, jobPosting._key]) : $web('jobs.clubs.jobs.show', [listItem.club._key, jobPosting._key])
	},
	companies: {
		key: 'companies',
		text: t('label.companies'),
		to: $web('jobs.companies.index'),
		apiUrl: $api('jobs.clubs.index'),
		params: {
			limit: 10,
			type: 'company',
			job_limit: jobLimitPerOwner
		},
		jobOwnerResolver: (listItem) => listItem.club,
		jobOwnerPathResolver: (listItem) => $web('jobs.companies.show', [listItem.club._key]),
		jobPathResolver: (listItem, jobPosting) => jobPosting.event ? $web('jobs.events.jobs.show', [jobPosting.event._key, jobPosting._key]) : $web('jobs.companies.jobs.show', [listItem.club._key, jobPosting._key])
	},
};

const activeTypePage = typePageConfigs[route.params.type];

const headerStyle = {
	title: activeTypePage.key !== 'all' ? `${t('label.job_portal')} ${t('conjunction.for')} ${activeTypePage.text}` : t('label.job_portal'),
	color: config.public.colors.jobs,
	itemLink: !inputFilterExists.value ? {
		to: '/operation-planner',
		text: t('label.operation_planner')
	} : undefined
};

const icons = {
	close: closeIcon,
	search: searchIcon,
	submit: submitIcon,
	next: nextIcon,
	filter: mdiFilterVariant
};

onMounted(() => {
	GeolocationService.locate((latLng) => {
		filterData.value = {...filterData.value, ...latLng};
	})
})

useMeta({
	title: t(`view.jobs.${activeTypePage.key}.title`),
	description: t(`view.jobs.${activeTypePage.key}.description`),
	canonicalQueries: Object.keys(filterData.value)
});
</script>

